import PlayerState from 'common/constants/playerState';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createIVSPlayer } from './createIVSPlayer';

const DURATION_CHANGED = 'PlayerDurationChanged';
const PLAYER_TIME_UPDATE = 'PlayerTimeUpdate';
const QUALITY_CHANGED = 'PlayerQualityChanged';

const VodPlayer = ({
    subTitles,
    videoId,
    src,
    onDurationChanged,
    onPlayerTimeUpdate,
    videoRef,
    className,
    playerRef,
    controls = true,
    muted,
    autoPlay,
    playsInline = true,
    loop,
    onQualityChanged,
    onReady,
    startTime,
}) => {
    const [videoElement, setVideoElement] = useState(null);
    const [player, setPlayer] = useState(null);
    const ref = useRef(null);

    const listeners = useRef({});

    const onSetPlayer = useCallback((player) => {
        setPlayer(player);
        ref.current = player;
    }, []);

    useEffect(() => {
        return () => {
            if (ref.current) {
                try {
                    ref.current.delete();
                } catch (e) {
                    console.warn('Error deleting player instance', e);
                }
            }
        };
    }, []);

    useEffect(() => {
        if (videoElement) {
            const player = createIVSPlayer();

            if (player) {
                player.setInitialBufferDuration(5);
                player.setAutoQualityMode(false);

                player.attachHTMLVideoElement(videoElement);
                if (startTime) {
                    const timestamp = Date.now();
                    videoElement.addEventListener('loadeddata', () => {
                        videoElement.currentTime = startTime + (Date.now() - timestamp) / 1000;
                    });
                }

                onSetPlayer(player);
                if (autoPlay) {
                    if (onReady) {
                        player.addEventListener(PlayerState.Ready, onReady);
                    }
                } else {
                    const handlePlaying = () => {
                        player.removeEventListener(PlayerState.Playing, handlePlaying);
                        player.pause();
                        player.seekTo(0);
                        player.setVolume(1);
                        onReady?.();
                    };
                    player.addEventListener(PlayerState.Playing, handlePlaying);
                    player.setVolume(0);
                    player.play();
                }
                // if (subTitles) {
                //     player.set;
                // }
            } else {
                console.error('IVSPlayer not supported');
            }
        }
    }, [autoPlay, onReady, onSetPlayer, startTime, videoElement]);

    useEffect(() => {
        if (player && src) {
            if (typeof playerRef === 'function') {
                playerRef(player);
            } else if (playerRef) {
                playerRef.current = player;
            }
            player.load(src);
        }
    }, [player, playerRef, src]);

    useEffect(() => {
        if (player) {
            if (onDurationChanged) {
                player.addEventListener(DURATION_CHANGED, (listeners.current[DURATION_CHANGED] = onDurationChanged));
            }
            const current = listeners.current[DURATION_CHANGED];
            return () => {
                if (current) {
                    player.removeEventListener(DURATION_CHANGED, current);
                }
            };
        }
    }, [player, onDurationChanged]);

    useEffect(() => {
        if (player) {
            if (onPlayerTimeUpdate) {
                player.addEventListener(
                    PLAYER_TIME_UPDATE,
                    (listeners.current[PLAYER_TIME_UPDATE] = onPlayerTimeUpdate),
                );
            }
            const current = listeners.current[PLAYER_TIME_UPDATE];
            return () => {
                if (current) {
                    player.removeEventListener(PLAYER_TIME_UPDATE, current);
                }
            };
        }
    }, [player, onPlayerTimeUpdate]);

    useEffect(() => {
        if (player) {
            if (onQualityChanged) {
                player.addEventListener(QUALITY_CHANGED, (listeners.current[QUALITY_CHANGED] = onQualityChanged));
            }
            const current = listeners.current[QUALITY_CHANGED];
            return () => {
                if (current) {
                    player.removeEventListener(QUALITY_CHANGED, current);
                }
            };
        }
    }, [player, onQualityChanged]);

    const catchElement = (element) => {
        setVideoElement(element);
        if (typeof videoRef === 'function') {
            videoRef(element);
        } else if (videoRef) {
            videoRef.current = element;
        }
    };

    return (
        <video
            id={videoId}
            ref={catchElement}
            controls={controls}
            crossOrigin="anonymous"
            className={className}
            playsInline={playsInline}
            muted={muted}
            autoPlay={autoPlay}
            loop={loop}
        >
            {subTitles?.map((sub) => (
                <track
                    key={sub.label}
                    label={sub.label}
                    default={sub.lang === 'es'}
                    kind="subtitles"
                    srcLang={sub.lang}
                    src={sub.src}
                ></track>
            ))}
        </video>
    );
};

export default VodPlayer;
